/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ApplicationInsights,
    IEventTelemetry,
    ITraceTelemetry,
    IExceptionTelemetry,
    IPageViewTelemetry,
    IDependencyTelemetry
} from '@microsoft/applicationinsights-web';
import { ITelemetryContext, ITraceLogger, ITraceLoggerParams, LogLevel } from '../interfaces';
import { IDictionary } from '../interfaces/IDictionary';
import { ITrackDependency } from '../interfaces/ITrackDependency';
import { TelemetryBase } from '../telemetry';
import { buildPropertiesList, getPropertiesFromContext, messageTemplateKey, shouldSkipLogging } from './app-insights-helper';
import { safeStringify } from './logging-helper';

export enum LoggerType {
    MSFTApplicationInsightsLogger,
    OperationalApplicationInsightsLogger
}

/**
 * Client-side logger that sends messages to Application Insights
 */
export class ClientAppInsightsJSLogger implements ITraceLogger {
    /**
     * Mapping between telemetry framework LogLevel and AppInsights JS Severity Levels
     */
    private static _severityLevelMapping: Map<LogLevel, number> = new Map([
        [LogLevel.Trace, 0],
        [LogLevel.Information, 1],
        [LogLevel.Warning, 2],
        [LogLevel.Error, 3],
        [LogLevel.Critical, 4]
    ]);

    private options: ITraceLoggerParams = <ITraceLoggerParams>{};
    private AppInsights: ApplicationInsights;
    private loggertype: LoggerType;

    /**
     * Creates a new instance of AppInsights Logger
     * @param loggerType The type of instance of Application Insights
     * @param instrumentationKey The instrumentation key for the Application Insights instance
     */
    constructor(loggerType: LoggerType, instrumentationKey: string) {
        const eventLog: IEventTelemetry = {
            name: 'Client version of Application Insights registered'
        };

        this.loggertype = loggerType;

        // @ts-ignore -- fetching globally available properties
        const loggerProps = window.___initialData___.requestContext;

        this.AppInsights = new ApplicationInsights({
            config: {
                instrumentationKey: instrumentationKey,
                enableCorsCorrelation: true,
                // @ts-ignore -- fetching globally available properties
                correlationHeaderDomains: window._loggerProps[0].correlationHeaderDomains
            }
        });

        this.AppInsights.loadAppInsights();

        if (this.loggertype === LoggerType.MSFTApplicationInsightsLogger) {
            this.addTelemetryInitializer(loggerProps);

            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const _this = this;
            // @ts-ignore -- globally providing the application insights trackException instance for logging
            window['_aiTrackExceptionInstance'] = exceptionLog => _this.AppInsights.trackException(exceptionLog);
        }

        if (this.loggertype === LoggerType.OperationalApplicationInsightsLogger) {
            this.operationalInsightsEventFilter();

            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const _this = this;
            // @ts-ignore -- globally providing the operational insights trackException instance for logging
            window['_oiTrackExceptionInstance'] = exceptionLog => _this.AppInsights.trackException(exceptionLog);
        }

        this.AppInsights.trackPageView();
        this.AppInsights.trackEvent(eventLog);

        // empty error queue if present
        // @ts-ignore
        const errors = window._globalErrors;
        if (errors && errors.length) {
            for (let i = 0; i < errors.length; i++) {
                this.AppInsights.trackException(errors[i]);
            }
        }
    }

    public initialize(options: ITraceLoggerParams): void {
        this.options = options;
    }

    /**
     * @internal
     * Logs a trace message to AppInsights
     * @param logLevel LogLevel of the message
     * @param context Telemetry context to add to log
     * @param messageTemplate Structured log formatted string
     * @param placeholders List of placeholders in messageTemplate along with position in string
     * @param values Arguments to replace placeholders
     * @param customTags Array of strings to attach to log
     */
    public sendLog(
        logLevel: LogLevel,
        context: ITelemetryContext,
        messageTemplate: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        placeholders: Map<string, any>,
        customTags?: string[],
        exception?: Error
    ): void {
        // Convert ITelemetry LogLevel to corresponding AppInsights SeverityLevel when logging
        const severityLevel = ClientAppInsightsJSLogger._severityLevelMapping.get(logLevel);

        if (shouldSkipLogging(severityLevel, this.options.isDebug)) {
            return;
        }

        // Build properties list with telemetry context and message template arguments
        const properties: IDictionary<string> = buildPropertiesList(context, placeholders, customTags);

        if (exception === undefined) {
            // Send log to AppInsights as trace event
            const traceLog: ITraceTelemetry = {
                message: messageTemplate,
                severityLevel: severityLevel
            };
            this.AppInsights.trackTrace(traceLog, properties);
        } else {
            // Attach message template as custom property
            properties[messageTemplateKey] = messageTemplate;

            const exceptionLog: IExceptionTelemetry = {
                exception: exception,
                properties: properties,
                severityLevel: severityLevel
            };
            // Send log to AppInsights as exception event
            this.AppInsights.trackException(exceptionLog);
        }
    }

    /**
     * @internal
     * Logs a trace message to AppInsights
     * @param logLevel Severity level of the log
     * @param message String to log
     * @param args Any additional objects to log
     * @param context Additional properties to add to the log
     */
    public log(logLevel: LogLevel, message: string, args: unknown[], context: ITelemetryContext): void {
        // Convert ITelemetry LogLevel to corresponding AppInsights SeverityLevel when logging
        const severityLevel = ClientAppInsightsJSLogger._severityLevelMapping.get(logLevel);

        if (shouldSkipLogging(severityLevel, this.options.isDebug)) {
            return;
        }

        // Append any extra arguments to the message
        let fullMessage = message;
        args.forEach(arg => {
            fullMessage = fullMessage.concat('\n', safeStringify(arg));
        });

        const traceLog: ITraceTelemetry = {
            message: `${logLevel}: ${fullMessage}`,
            properties: getPropertiesFromContext(context)
        };
        this.AppInsights.trackTrace(traceLog);
    }
    /**
     * @internal
     * Logs an error object to AppInsights as an exception
     * @param error Error object to log
     * @param context Additional properties to add to the log
     */
    public exception(error: Error, context: ITelemetryContext): void {
        const exceptionLog: IExceptionTelemetry = {
            exception: error,
            properties: getPropertiesFromContext(context)
        };
        this.AppInsights.trackException(exceptionLog);
    }
    /**
     * @internal
     * No-op - not tracking client side metrics
     * @param name name of the metric
     * @param value value of the metric
     */
    public trackMetric(name: string, value: number, context: ITelemetryContext): void {
        // noop
    }
    /**
     * Track a structured event with app insights
     *
     * @template TProperties interface for properties
     * @template TMeasurements interface for measurements to go with the event
     * @param {ITrackEventOptions<TProperties, TMeasurements>} trackEventOptions event options
     */
    public trackEvent<TProperties, TMeasurements>(
        name: string,
        properties?: TProperties,
        measurements?: TMeasurements,
        context?: ITelemetryContext
    ): void {
        const eventLog: IEventTelemetry = {
            name: name,
            properties: <IDictionary<string>>(<unknown>properties),
            measurements: <IDictionary<number>>(<unknown>measurements)
        };
        this.AppInsights.trackEvent(eventLog);
    }

    /**
     * Track a page view
     *
     * @param {string} name
     * @param {string} [url]
     */
    public trackPageView(name: string, url?: string): void {
        const pageViewLog: IPageViewTelemetry = {
            name: name,
            uri: url
        };
        this.AppInsights.trackPageView(pageViewLog);
    }

    /**
     * Track request
     * @param options request options
     */
    public trackDependency(options: ITrackDependency): void {
        const id = TelemetryBase.generateGuid();

        const dependencyLog: IDependencyTelemetry = {
            id: id,
            type: options.method,
            target: `https://${options.target}${options.name}` || '',
            name: options.name || '',
            duration: options.duration,
            success: options.success,
            responseCode: options.resultCode,
            properties: options.properties
        };
        this.AppInsights.trackDependencyData(dependencyLog);
    }

    private operationalInsightsEventFilter() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const telemetryInitializer = (envelope: any) => {
            const MessageString = 'MessageData';
            const EventString = 'EventData';
            const RemoteDependencyDataString = 'RemoteDependencyData';

            if (envelope) {
                const baseType = envelope.baseType;
                if (baseType === MessageString || baseType === EventString) {
                    return false;
                }
                // All retail server calls will have requestId set. if no requestId set, drop it
                if (baseType === RemoteDependencyDataString && !envelope.baseData.properties.requestId) {
                    return false;
                }

                return true;
            }
        };

        this.AppInsights.addTelemetryInitializer(telemetryInitializer);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private addTelemetryInitializer(loggerProps: any) {
        const IS_DEV = process.env.NODE_ENV === 'development';
        const sdkVersion = loggerProps._debug.commerceSDKVersion;
        const sskVersion = loggerProps._debug.commerceSSKVersion || 'XX';
        const retailProxyVersion = loggerProps._debug.retailProxyVersion || 'XX';
        let environmentId = 'Development';
        let commerceCoreEnvironmentId = 'Development';

        if (!IS_DEV) {
            environmentId = loggerProps.telemetrySettings.environmentId;
            commerceCoreEnvironmentId = loggerProps.telemetrySettings.commerceCoreEnvId || 'CommerceCore_EnvironmentId_Missing';
        }

        const pageViewDataString = 'PageviewData';
        const pageviewPerformanceDataString = 'PageviewPerformanceData';
        const remoteDependencyDataString = 'RemoteDependencyData';
        const exceptionDataString = 'ExceptionData';
        const censoredString = 'CENSORED';

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const telemetryInitializer = (envelope: any) => {
            const telemetryItem = envelope.baseData;
            telemetryItem.properties = telemetryItem.properties || {};

            if (envelope) {
                const baseType = envelope.baseType;
                // we add more client performance metrics to PageviewPerformanceData
                if (baseType === pageviewPerformanceDataString) {
                    const performanceNavigationTimming = window && window.performance.getEntriesByType('navigation');
                    const performancePaintTiming = window.performance.getEntriesByType('paint');
                    // add performance metrics
                    if (performanceNavigationTimming && performanceNavigationTimming.length > 0) {
                        const performanceNavigationTimmingData = performanceNavigationTimming[0];
                        const duration = performanceNavigationTimmingData.duration;
                        // @ts-ignore -- Content Load Event Start
                        const domContentLoadedEventStart = performanceNavigationTimmingData.domContentLoadedEventStart;
                        // @ts-ignore -- Content Load Event End
                        const domContentLoadedEventEnd = performanceNavigationTimmingData.domContentLoadedEventEnd;
                        // @ts-ignore
                        const domInteractive = performanceNavigationTimmingData.domInteractive;
                        // @ts-ignore -- performance Navigation complete duration
                        const domComplete = performanceNavigationTimmingData.domComplete;
                        duration ? (telemetryItem.properties.duration = duration) : null;
                        domContentLoadedEventStart && domContentLoadedEventEnd
                            ? (telemetryItem.properties.domContentLoadedEvent = domContentLoadedEventEnd - domContentLoadedEventStart)
                            : null;
                        domInteractive ? (telemetryItem.properties.domInteractive = domInteractive) : null;
                        domComplete ? (telemetryItem.properties.domComplete = domComplete) : null;
                    }
                    // add painting perf metrics
                    if (performancePaintTiming && performancePaintTiming.length > 0) {
                        performancePaintTiming.forEach(paintTiming => {
                            telemetryItem.properties[paintTiming.name] = paintTiming.startTime;
                        });
                    }
                }

                // To check the telemetry item’s type
                if (baseType === pageViewDataString || baseType === pageviewPerformanceDataString) {
                    // this statement removes url from all page view documents
                    telemetryItem.uri = `URL ${censoredString}`;
                    // @ts-ignore
                    telemetryItem.properties['ExperimentVariants'] = window._loggerProps[0].expVariantInfo;
                }
                // All retail server calls will have requestId set. if no requestId set, censor the strings
                if (baseType === remoteDependencyDataString && !telemetryItem.properties.requestId) {
                    return false;
                }

                if (envelope.tags) {
                    if (
                        baseType === exceptionDataString ||
                        baseType === pageviewPerformanceDataString ||
                        baseType === pageViewDataString ||
                        baseType === remoteDependencyDataString
                    ) {
                        envelope.tags['ai.operation.name'] = censoredString;
                    }
                }
            }

            const props = telemetryItem.properties;
            props['Environment id'] = environmentId;
            props['Commerce core environment id'] = commerceCoreEnvironmentId;
            props['SDK Version'] = sdkVersion;
            props['RetailProxy Version'] = retailProxyVersion;
            // @ts-ignore
            props['Node Process ID'] = window._loggerProps[0].processID;
            props['SSK Version'] = sskVersion;
            props['Server operation id'] = loggerProps.telemetrySettings.operationId;
            // @ts-ignore
            props['DA SDK Version'] = window && window.oneDSWeb && window.oneDSWeb.config && window.oneDSWeb.config._daSdkVer;
        };

        this.AppInsights.addTelemetryInitializer(telemetryInitializer);
    }
}
